import { useState, useEffect } from 'react';
import { type Draft, type Article, type FeedbackAction } from '@/lib/types';
import { cn } from '@/lib/cn';
import { useRegenerateDraft } from '@/hooks/useDraft';
import { useFeedback } from '@/hooks/useFeedback';
import { useConversationStore } from '@/stores/conversationStore';
import { wasTextEdited } from '@/lib/editDistance';
import FeedbackButtons from './FeedbackButtons';
import { Badge } from './ui/badge';

interface DraftEditorProps {
  draft: Draft;
  onInsert: (text: string) => void;
  onDraftUpdate?: (draft: Draft) => void;
}

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'formal', label: 'Formal' },
  { value: 'casual', label: 'Casual' },
] as const;

const LENGTHS = [
  { value: 'shorter', label: 'Shorter' },
  { value: 'same', label: 'Same Length' },
  { value: 'longer', label: 'Longer' },
] as const;

export default function DraftEditor({
  draft,
  onInsert,
  onDraftUpdate,
}: DraftEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [showArticles, setShowArticles] = useState(false);
  const [selectedTone, setSelectedTone] = useState<string>(draft.tone || 'professional');
  const [selectedLength, setSelectedLength] = useState<string>('same');
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [showRegenerateForm, setShowRegenerateForm] = useState(false);
  const [regenerateComment, setRegenerateComment] = useState('');

  const regenerateMutation = useRegenerateDraft();
  const feedbackMutation = useFeedback();

  const originalDraftText = useConversationStore((state) => state.originalDraftText);
  const feedbackSubmitted = useConversationStore((state) => state.feedbackSubmitted);

  // Keyboard shortcut for thumbs up (Cmd+Shift+U)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.metaKey && e.shiftKey && e.key === 'U' && !feedbackSubmitted) {
        e.preventDefault();
        handleFeedbackSubmit('sent_as_is');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [feedbackSubmitted]);

  const handleStartEdit = () => {
    setEditedText(draft.text);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedText('');
  };

  const handleInsert = () => {
    const textToInsert = isEditing ? editedText : draft.text;
    onInsert(textToInsert);

    // Auto-track feedback if not already submitted
    if (!feedbackSubmitted && originalDraftText) {
      const wasEdited = wasTextEdited(originalDraftText, textToInsert);
      const action: FeedbackAction = wasEdited ? 'edited_before_sending' : 'sent_as_is';

      // Submit feedback automatically
      feedbackMutation.mutate({
        action,
        currentDraftText: textToInsert,
      });
    }
  };

  const handleFeedbackSubmit = (action: FeedbackAction, feedbackText?: string) => {
    const currentText = isEditing ? editedText : draft.text;

    feedbackMutation.mutate({
      action,
      feedbackText,
      currentDraftText: currentText,
    });
  };

  const handleCopyToClipboard = async () => {
    const textToCopy = isEditing ? editedText : draft.text;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleRegenerate = () => {
    if (!draft.id) {
      console.error('Draft ID is required for regeneration');
      return;
    }

    const options: { tone?: string; length?: string; comment?: string } = {};
    let regenerationReason: 'tone' | 'length' | 'other' | undefined;

    if (selectedTone !== draft.tone) {
      options.tone = selectedTone;
      regenerationReason = 'tone';
    }
    if (selectedLength !== 'same') {
      options.length = selectedLength;
      regenerationReason = 'length';
    }
    if (regenerateComment.trim()) {
      options.comment = regenerateComment.trim();
      if (!regenerationReason) {
        regenerationReason = 'other';
      }
    }

    // Submit feedback ONLY if we have valid tracking data
    const shouldSubmitFeedback = !feedbackSubmitted &&
                                  regenerationReason &&
                                  originalDraftText &&
                                  draft.id;

    regenerateMutation.mutate(
      { draftId: draft.id, options },
      {
        onSuccess: (data) => {
          // Submit feedback for the OLD draft after regeneration succeeds
          if (shouldSubmitFeedback) {
            feedbackMutation.mutate({
              action: 'regenerated',
              regenerationReason,
              currentDraftText: originalDraftText, // Use the ORIGINAL text, not the new one
            });
          }

          // Update with the new draft
          if (onDraftUpdate) {
            const updatedDraft = {
              ...data.draft,
              articles: data.articles_used || [],
              confidence: data.confidence || data.draft.confidence,
            };
            onDraftUpdate(updatedDraft);
          }
          setShowRegenerateForm(false);
          setRegenerateComment('');
        },
      }
    );
  };

  const confidencePercent = Math.round(draft.confidence * 100);
  const confidenceColor =
    draft.confidence > 0.8
      ? 'text-green-600'
      : draft.confidence > 0.6
        ? 'text-yellow-600'
        : 'text-orange-600';

  const isRegenerating = regenerateMutation.isPending;
  const isImportedDraft = draft.id.startsWith('imported_'); // Not AI-generated

  return (
    <div className="rounded-lg border p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">Draft Response</h3>
            {feedbackSubmitted && (
              <Badge variant="secondary" className="text-xs">
                Feedback Submitted
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={cn('text-xs font-medium', confidenceColor)}>
              {confidencePercent}% confidence
            </span>
            {draft.tone && (
              <span className="text-xs text-muted-foreground capitalize">
                • {draft.tone}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Draft Text */}
      <div className="relative">
        {isEditing ? (
          <textarea
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            className="w-full min-h-[200px] px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y"
            placeholder="Edit your draft here..."
          />
        ) : (
          <div className="prose prose-sm max-w-none">
            <div className="whitespace-pre-wrap text-sm p-3 rounded-lg bg-muted">
              {draft.text}
            </div>
          </div>
        )}
      </div>

      {/* Tone & Length Controls */}
      {!isEditing && (
        <div className="grid grid-cols-2 gap-3 border-t pt-4">
          <div>
            <label className="block text-xs font-medium mb-2">Tone</label>
            <select
              value={selectedTone}
              onChange={(e) => setSelectedTone(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isRegenerating}
            >
              {TONES.map((tone) => (
                <option key={tone.value} value={tone.value}>
                  {tone.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-2">Length</label>
            <select
              value={selectedLength}
              onChange={(e) => setSelectedLength(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isRegenerating}
            >
              {LENGTHS.map((length) => (
                <option key={length.value} value={length.value}>
                  {length.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Articles Used */}
      {draft.articles && draft.articles.length > 0 && (
        <div className="border-t pt-4">
          <button
            onClick={() => setShowArticles(!showArticles)}
            className="flex items-center gap-2 text-sm font-medium hover:underline"
          >
            <span>
              {showArticles ? '▼' : '▶'} {draft.articles.length} help article
              {draft.articles.length !== 1 ? 's' : ''} referenced
            </span>
          </button>

          {showArticles && (
            <div className="mt-3 space-y-2">
              {draft.articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Feedback Section - Only for AI-generated drafts */}
      {!feedbackSubmitted && !isImportedDraft && (
        <div className="border-t pt-4">
          <FeedbackButtons
            onSubmit={handleFeedbackSubmit}
            disabled={feedbackSubmitted}
            isSubmitting={feedbackMutation.isPending}
          />
        </div>
      )}

      {/* Actions */}
      <div className="border-t pt-4 space-y-2">
        {isEditing ? (
          <div className="flex gap-2">
            <button
              onClick={handleCancelEdit}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border bg-background hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={handleInsert}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Insert Edited Draft
            </button>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <button
                onClick={handleStartEdit}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border bg-background hover:bg-accent"
                disabled={isRegenerating}
              >
                Edit
              </button>
              <button
                onClick={() => setShowRegenerateForm(!showRegenerateForm)}
                disabled={isRegenerating}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRegenerating ? 'Regenerating...' : 'Regenerate'}
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCopyToClipboard}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border bg-background hover:bg-accent"
                disabled={isRegenerating}
              >
                {copiedToClipboard ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={handleInsert}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={isRegenerating}
              >
                Insert into Front
              </button>
            </div>
          </>
        )}
      </div>

      {/* Regenerate Form - Inline Collapsible */}
      {showRegenerateForm && !isEditing && (
        <div className="border-t pt-4 space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Custom feedback (optional)
            </label>
            <textarea
              value={regenerateComment}
              onChange={(e) => setRegenerateComment(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleRegenerate();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setShowRegenerateForm(false);
                  setRegenerateComment('');
                }
              }}
              placeholder="e.g., 'Make it more empathetic' or 'Add information about refund policy'"
              className="w-full min-h-[80px] px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Or use the tone/length selectors above. Press Cmd+Enter to regenerate, Esc to cancel.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowRegenerateForm(false);
                setRegenerateComment('');
              }}
              disabled={isRegenerating}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border bg-background hover:bg-accent disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRegenerate}
              disabled={isRegenerating}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isRegenerating ? 'Regenerating...' : 'Regenerate Now'}
            </button>
          </div>
        </div>
      )}

      {/* Regeneration Error */}
      {regenerateMutation.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-800">
            Failed to regenerate draft. Please try again.
          </p>
        </div>
      )}
    </div>
  );
}

function ArticleCard({ article }: { article: Article }) {
  const relevancePercent = Math.round(article.relevance * 100);

  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-3 rounded-lg border bg-background hover:bg-accent transition-colors"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <h4 className="text-sm font-medium line-clamp-1">{article.title}</h4>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {relevancePercent}% relevant
        </span>
      </div>
      <p className="text-xs text-muted-foreground line-clamp-2">
        {article.body}
      </p>
    </a>
  );
}
